import React, { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import './FilterPanel.css';

/**
 * fields: Array of field descriptors:
 * {
 *   key: string,           — key in the draft/values objects
 *   label: string,         — row label
 *   options: string[],     — display options
 *   toValue?: (label) => any,   — convert display label to stored value (default: identity)
 *   toLabel?: (value) => string,— convert stored value to display label (default: identity)
 *   defaultValue: any,     — value used for reset and active detection
 *   disabled?: boolean,
 * }
 */
function FilterPanel({ fields, values, onApply }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const wrapperRef = useRef(null);

  const handleOpen = () => {
    const initial = {};
    fields.forEach(f => { initial[f.key] = values[f.key]; });
    setDraft(initial);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  const handleReset = () => {
    const reset = {};
    fields.forEach(f => { reset[f.key] = f.defaultValue; });
    setDraft(reset);
    onApply(reset);
    setOpen(false);
  };

  const activeCount = fields.filter(f => {
    const val = values[f.key];
    return JSON.stringify(val) !== JSON.stringify(f.defaultValue);
  }).length;
  const isActive = activeCount > 0;

  return (
    <div className="filter-panel-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`filter-panel-btn${isActive ? ' filter-panel-btn--active' : ''}`}
        onClick={open ? () => setOpen(false) : handleOpen}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && <span className="filter-panel-badge">{activeCount}</span>}
      </button>

      {open && (
        <div className="filter-panel-dropdown">
          {fields.map(f => {
            const toLabel = f.toLabel || (v => v);
            const toValue = f.toValue || (l => l);
            return (
              <div key={f.key} className="filter-panel-row">
                <label className="filter-panel-label">{f.label}</label>
                <select
                  className="filter-panel-select"
                  value={toLabel(draft[f.key])}
                  onChange={(e) => setDraft(d => ({ ...d, [f.key]: toValue(e.target.value) }))}
                  disabled={f.disabled}
                >
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            );
          })}

          <div className="filter-panel-actions">
            <button type="button" className="filter-panel-reset" onClick={handleReset}>Reset</button>
            <button type="button" className="filter-panel-apply" onClick={handleApply}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;
